import React from "react";
import { Button } from "../ui/button";
import {
    ImageSquare,
    Rocket,
    Trash,
    ClipboardText,
    PencilSimple,
    Check,
    X,
    FileXls,
    Eye,
    Archive,
    ListChecks,
} from "@phosphor-icons/react";

function imageButtonLabel(imageBusy, hasImage) {
    if (imageBusy) return "Generating…";
    return hasImage ? "Regenerate image" : "Generate image";
}

/**
 * Action buttons row for the ChallengeDetail dialog.
 * Pure presentational — handlers are wired by the parent.
 */
export default function ChallengeDetailActions({
    challenge,
    editing,
    busy,
    imageBusy,
    hasImage,
    validation,
    onEdit,
    onSave,
    onCancel,
    onPublish,
    onDelete,
    onGenImage,
    onCopyJson,
    onCopyImportJson,
    onPreview,
    onValidate,
    onReadyForReview,
    onArchive,
    onApprove,
    onReject,
}) {
    const canPublish = validation ? validation.is_valid : true;
    const isArchived = challenge.status === "archived";
    const isPublished = challenge.status === "published";
    const isCommunity = challenge.status === "community_review";
    return (
        <div className="flex flex-wrap gap-2">
            {isCommunity && (
                <>
                    <Button
                        onClick={onApprove}
                        disabled={busy}
                        className="rounded-none bg-fa-green/10 text-fa-green border border-fa-green/40 hover:bg-fa-green hover:text-black uppercase font-bold"
                        data-testid="approve-btn"
                    >
                        <Check size={14} weight="bold" /> Approve
                    </Button>
                    <Button
                        onClick={onReject}
                        disabled={busy}
                        className="rounded-none bg-fa-red/10 text-fa-red border border-fa-red/40 hover:bg-fa-red hover:text-white uppercase font-bold"
                        data-testid="reject-btn"
                    >
                        <X size={14} weight="bold" /> Reject
                    </Button>
                </>
            )}
            {editing ? (
                <>
                    <Button
                        onClick={onSave}
                        disabled={busy}
                        className="rounded-none bg-fa-yellow hover:bg-[#CBE815] text-black uppercase font-bold"
                        data-testid="save-edits-btn"
                    >
                        <Check size={14} weight="bold" /> Save
                    </Button>
                    <Button
                        variant="outline"
                        onClick={onCancel}
                        className="rounded-none border-white/20 bg-transparent hover:bg-white/5 uppercase font-bold"
                        data-testid="cancel-edits-btn"
                    >
                        <X size={14} weight="bold" /> Cancel
                    </Button>
                </>
            ) : (
                <Button
                    onClick={onEdit}
                    className="rounded-none bg-transparent border border-white/20 hover:border-white/50 hover:bg-white/5 text-white uppercase font-bold"
                    data-testid="edit-btn"
                >
                    <PencilSimple size={14} weight="bold" /> Edit
                </Button>
            )}

            <Button
                onClick={onPreview}
                className="rounded-none bg-transparent border border-fa-yellow/50 hover:bg-fa-yellow hover:text-black text-fa-yellow uppercase font-bold"
                data-testid="preview-btn"
            >
                <Eye size={14} weight="bold" /> Preview as player
            </Button>

            <Button
                onClick={onValidate}
                disabled={busy}
                className="rounded-none bg-transparent border border-white/20 hover:border-white/50 hover:bg-white/5 text-white uppercase font-bold"
                data-testid="validate-btn"
            >
                <ListChecks size={14} weight="bold" /> Validate
            </Button>

            {!isPublished && !isArchived && (
                <Button
                    onClick={onReadyForReview}
                    disabled={busy}
                    className="rounded-none bg-fa-cyan/10 text-fa-cyan border border-fa-cyan/40 hover:bg-fa-cyan hover:text-black uppercase font-bold"
                    data-testid="ready-for-review-btn"
                >
                    Ready for review
                </Button>
            )}

            {!isPublished && !isArchived && (
                <Button
                    onClick={onPublish}
                    disabled={busy || !canPublish}
                    title={canPublish ? "Publish" : "Fix validation errors first"}
                    className="rounded-none bg-fa-green/10 text-fa-green border border-fa-green/40 hover:bg-fa-green hover:text-black uppercase font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="publish-btn"
                >
                    <Rocket size={14} weight="bold" /> Publish
                </Button>
            )}

            <Button
                onClick={onGenImage}
                disabled={imageBusy}
                className="rounded-none bg-fa-cyan/10 text-fa-cyan border border-fa-cyan/40 hover:bg-fa-cyan hover:text-black uppercase font-bold"
                data-testid="gen-image-btn"
            >
                <ImageSquare size={14} weight="bold" />{" "}
                {imageButtonLabel(imageBusy, hasImage)}
            </Button>

            <Button
                onClick={onCopyJson}
                className="rounded-none bg-transparent border border-white/20 hover:border-white/50 hover:bg-white/5 text-white uppercase font-bold"
                data-testid="copy-json-btn"
            >
                <ClipboardText size={14} weight="bold" /> Copy JSON
            </Button>

            <Button
                onClick={onCopyImportJson}
                className="rounded-none bg-fa-yellow/10 text-fa-yellow border border-fa-yellow/40 hover:bg-fa-yellow hover:text-black uppercase font-bold"
                data-testid="copy-import-json-btn"
            >
                <ClipboardText size={14} weight="bold" /> Copy Import JSON
            </Button>

            <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/challenges/${challenge.id}/export.xlsx`}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-none bg-fa-green/10 text-fa-green border border-fa-green/40 hover:bg-fa-green hover:text-black uppercase font-bold px-3 py-2 text-xs flex items-center gap-1.5"
                data-testid="download-xlsx-btn"
            >
                <FileXls size={14} weight="bold" /> Download XLSX
            </a>

            {!isArchived && (
                <Button
                    onClick={onArchive}
                    disabled={busy}
                    className="rounded-none bg-transparent border border-white/20 hover:border-white/50 hover:bg-white/5 text-white/70 uppercase font-bold"
                    data-testid="archive-btn"
                >
                    <Archive size={14} weight="bold" /> Archive
                </Button>
            )}

            <Button
                onClick={onDelete}
                disabled={busy}
                className="rounded-none bg-fa-red/10 text-fa-red border border-fa-red/40 hover:bg-fa-red hover:text-white uppercase font-bold ml-auto"
                data-testid="delete-btn"
            >
                <Trash size={14} weight="bold" /> Delete
            </Button>
        </div>
    );
}
